import { NextStepButton } from "@/components/FormButtons/NextStepButton"
import FormLabelRequired from "@/components/FormLabel/FormLabelRequired"
import { RequiredErrorMessage } from "@/components/RequiredErrorMessage"
import SingleField from "@/components/Sections/SingleField"
import { useStepperContext } from "@/components/Stepper/StepperContext"
import { ProjectSubtype, ProjectType } from "@/domain/project/constants"
import { PropertyTypes } from "@/domain/property/constants"
import useLoadRefLabels from "@/hooks/useLoadRefLabels"
import type { InterBuPostInterBuInputJsonld } from "@/networking/api"
import { useGetReactSelectStyles } from "@/theme/components-style-overrides/reactselect-definition"
import parseNumber from "@/utils/converter/parseNumber"
import { extractFieldsFrom, isNullOrUndefined, mergeObjects } from "@/utils/ts-utils"
import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Textarea,
} from "@chakra-ui/react"
import { useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import ReactSelect, { createFilter } from "react-select"
import { AddressAutocomplete } from "../../components/AddressAutocomplete"
import { useCreationMultiStepFormContext } from "../../context/ShareRequestInterbuCreateModalContext"
import type { GeocodedAddress } from "../../helpers/photon"
import { getRefLabelOptions } from "../../helpers/utils"

type StepForm = {
  project_maturity?: string | null
  type: string
  subtype: string
  property_type: string
  message?: string | null
  price_unit?: string | null
  price_min?: number | null
  price_max?: number | null
  surface_min?: number | null
  surface_max?: number | null
  surface_unit?: string | null
  address_1?: string | null
  zipcode?: string | null
  city?: string | null
  country?: string | null
  project_comment?: string | null
}

type WatchedValuesType = Pick<InterBuPostInterBuInputJsonld, "type" | "subtype">

// The property type is only asked for real estate, but the API requires one for every activity.
export const defaultPropertyTypeFor = (activity?: string | null) =>
  activity === ProjectSubtype.REAL_ESTATE ? PropertyTypes.HOUSE : PropertyTypes.OTHER

// An indicative price for a real estate offer, a budget for everything else.
export const priceLabelId = (type?: string | null, subtype?: string | null) =>
  type === ProjectSubtype.REAL_ESTATE && subtype === ProjectType.OFFER
    ? "userSharedData.interbu.modal.steps.1.price"
    : "userSharedData.interbu.modal.steps.1.budget"

export default () => {
  const intl = useIntl()
  const { goToNextStep } = useStepperContext()
  const { formState, setFormState, setAddressLocation } = useCreationMultiStepFormContext()

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<StepForm>({
    defaultValues: {
      ...extractFieldsFrom(formState, [
        "project_maturity",
        "type",
        "subtype",
        "property_type",
        "message",
        "price_min",
        "price_max",
        "price_unit",
        "surface_min",
        "surface_max",
        "surface_unit",
        "address_1",
        "zipcode",
        "city",
        "country",
        "project_comment",
      ]),
    },
  })

  const [watchedValues, setWatchedValues] = useState<WatchedValuesType>({
    ...extractFieldsFrom(formState, ["type", "subtype"]),
  })
  const setWatchedValue = (newValues: { [K in keyof WatchedValuesType]: WatchedValuesType[K] }) => {
    const values = mergeObjects({ ...watchedValues }, newValues)
    setWatchedValues(values)
  }

  const isRealEstate = useMemo(
    () => watchedValues.type === ProjectSubtype.REAL_ESTATE,
    [watchedValues]
  )
  const isOffer = useMemo(() => watchedValues.subtype === ProjectType.OFFER, [watchedValues])

  // Project maturity

  const {
    labels: projectMaturity,
    isLoading: isLoadingProjectMaturity,
    isFetching: isFetchingProjectMaturity,
  } = useLoadRefLabels("project:maturity:")

  const projectMaturityOptions = useMemo(
    () => getRefLabelOptions(projectMaturity, intl, "code"),
    [projectMaturity, intl]
  )

  // Field of activity

  const {
    labels: projectActivity,
    isLoading: isLoadingProjectActivity,
    isFetching: isFetchingProjectActivity,
  } = useLoadRefLabels("project:activity:")

  const projectActivityOptions = useMemo(
    () => getRefLabelOptions(projectActivity, intl, "code"),
    [projectActivity, intl]
  )

  // Project type

  const {
    labels: projectType,
    isLoading: isLoadingProjectType,
    isFetching: isFetchingProjectType,
  } = useLoadRefLabels("project:type:", undefined, { query: { enabled: isRealEstate } })

  const projectTypeOptions = useMemo(
    () =>
      getRefLabelOptions(
        projectType.filter(lbl => lbl.code !== ProjectType.SERVICE),
        intl,
        "code"
      ),
    [projectType, intl]
  )

  // Property type

  const {
    labels: propertyType,
    isLoading: isLoadingPropertyType,
    isFetching: isFetchingPropertyType,
  } = useLoadRefLabels("property:type:", undefined, { query: { enabled: isRealEstate } })

  const propertyTypeOptions = useMemo(
    () => getRefLabelOptions(propertyType, intl, "code"),
    [propertyType, intl]
  )

  // Currencies

  const {
    labels: currencies,
    isLoading: isLoadingCurrencies,
    isFetching: isFetchingCurrencies,
  } = useLoadRefLabels("unit:currency:")

  const currenciesOptions = useMemo(
    () => getRefLabelOptions(currencies, intl, "code"),
    [currencies, intl]
  )

  // Area units

  const {
    labels: unitsArea,
    isLoading: isLoadingUnitsArea,
    isFetching: isFetchingUnitsArea,
  } = useLoadRefLabels("unit:area:")

  const unitsAreaOptions = useMemo(
    () => getRefLabelOptions(unitsArea, intl, "code"),
    [unitsArea, intl]
  )

  // Countries (ref-label codes are ISO 3166-1 alpha-2, like Photon's country codes)

  const {
    labels: countries,
    isLoading: isLoadingCountries,
    isFetching: isFetchingCountries,
  } = useLoadRefLabels("country:")

  const countriesOptions = useMemo(
    () => getRefLabelOptions(countries, intl, "code"),
    [countries, intl]
  )

  // A picked suggestion fills the structured fields, which stay editable.
  const onAddressSelect = (address: GeocodedAddress) => {
    setValue("zipcode", address.zipcode ?? "")
    setValue("city", address.city ?? "")
    if (countriesOptions.some(opt => opt.value === address.countryCode))
      setValue("country", address.countryCode, { shouldValidate: true })
    setAddressLocation(address.location)
  }

  // onSubmit

  // Min / max ranges: either bound may be empty, the maximum may not be below the minimum.
  const maxNotBelowMin =
    (minField: "price_min" | "surface_min") => (max: number | null | undefined) => {
      const [minValue, maxValue] = [parseNumber(getValues(minField)), parseNumber(max)]
      return isNullOrUndefined(minValue) || isNullOrUndefined(maxValue) || minValue <= maxValue
        ? true
        : intl.formatMessage({ id: "userSharedData.interbu.modal.steps.1.range.maxBelowMin" })
    }

  const onSubmit = (stepFormData: StepForm) => {
    setFormState(prevFormState => ({
      ...prevFormState,
      ...stepFormData,
      price_min: parseNumber(stepFormData.price_min),
      price_max: parseNumber(stepFormData.price_max),
      surface_min: parseNumber(stepFormData.surface_min),
      surface_max: parseNumber(stepFormData.surface_max),
    }))

    goToNextStep()
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} maxW="100%" margin="auto">
      <Flex direction="column" gap="5" my="8">
        <SingleField
          label={intl.formatMessage({
            id: "userSharedData.interbu.modal.steps.1.localization",
          })}
        >
          <Grid templateColumns="repeat(6, 1fr)" gap={2} width="100%">
            <GridItem colSpan={6}>
              <FormLabel>
                <FormattedMessage id="userSharedData.interbu.modal.steps.1.localization.address" />
              </FormLabel>
              <Controller
                control={control}
                name="address_1"
                render={({ field: { value, onChange }, fieldState }) => (
                  <AddressAutocomplete
                    value={value ?? ""}
                    onChange={address => {
                      onChange(address)
                      // Typed by hand: the previous coordinates no longer match.
                      setAddressLocation(null)
                    }}
                    onSelect={onAddressSelect}
                    fieldState={fieldState}
                  />
                )}
              />
            </GridItem>
            <GridItem>
              <FormLabel>
                <FormattedMessage id="userSharedData.interbu.modal.steps.1.localization.zipcode" />
              </FormLabel>
              <Input {...register("zipcode")} />
            </GridItem>
            <GridItem colSpan={3}>
              <FormLabel>
                <FormattedMessage id="userSharedData.interbu.modal.steps.1.localization.city" />
              </FormLabel>
              <Input {...register("city")} />
            </GridItem>
            <GridItem colSpan={2}>
              <FormLabel>
                <FormattedMessage id="userSharedData.interbu.modal.steps.1.localization.country" />
                <FormLabelRequired />
              </FormLabel>
              <FormControl isInvalid={!!errors.country}>
                <Controller
                  control={control}
                  name="country"
                  rules={{ required: true }}
                  render={({ field: { value, onChange, onBlur, ref }, fieldState }) => (
                    <ReactSelect
                      ref={ref}
                      onBlur={onBlur}
                      filterOption={createFilter({
                        ignoreCase: true,
                        ignoreAccents: true,
                        matchFrom: "any",
                        trim: true,
                      })}
                      options={countriesOptions}
                      value={countriesOptions.filter(opt => value === opt.value)}
                      onChange={newVal => onChange(newVal?.value)}
                      styles={useGetReactSelectStyles(fieldState)}
                      isLoading={isLoadingCountries || isFetchingCountries}
                      loadingMessage={() => intl.formatMessage({ id: "loading.generic.title" })}
                    />
                  )}
                />
                <RequiredErrorMessage />
              </FormControl>
            </GridItem>
          </Grid>
        </SingleField>
        {/* The property address, then the request itself. */}
        <Divider />
        <SingleField
          label={intl.formatMessage({
            id: "userSharedData.interbu.modal.steps.1.projectActivity",
          })}
          required
        >
          <FormControl isInvalid={!!errors.type}>
            <Controller
              control={control}
              name="type"
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur, ref }, fieldState }) => (
                <ReactSelect
                  ref={ref}
                  onBlur={onBlur}
                  options={projectActivityOptions}
                  value={projectActivityOptions.filter(opt => value === opt.value)}
                  onChange={newVal => {
                    const type = newVal?.value ?? formState.type
                    const subtype =
                      newVal?.value === ProjectSubtype.REAL_ESTATE
                        ? ProjectType.OFFER
                        : ProjectType.SERVICE
                    setWatchedValue({ type, subtype })
                    setValue("subtype", subtype)
                    setValue("property_type", defaultPropertyTypeFor(type))
                    onChange(type)
                  }}
                  styles={useGetReactSelectStyles(fieldState)}
                  isLoading={isLoadingProjectActivity || isFetchingProjectActivity}
                  loadingMessage={() => intl.formatMessage({ id: "loading.generic.title" })}
                />
              )}
            />
            <RequiredErrorMessage />
          </FormControl>
        </SingleField>
        <SingleField
          label={intl.formatMessage({
            id: "userSharedData.interbu.modal.steps.1.projectMaturity",
          })}
          required
        >
          <FormControl isInvalid={!!errors.project_maturity}>
            <Controller
              control={control}
              name="project_maturity"
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur, ref }, fieldState }) => (
                <ReactSelect
                  ref={ref}
                  onBlur={onBlur}
                  options={projectMaturityOptions}
                  value={projectMaturityOptions.filter(opt => value === opt.value)}
                  onChange={newVal => onChange(newVal?.value)}
                  styles={useGetReactSelectStyles(fieldState)}
                  isLoading={isLoadingProjectMaturity || isFetchingProjectMaturity}
                  loadingMessage={() => intl.formatMessage({ id: "loading.generic.title" })}
                />
              )}
            />
            <RequiredErrorMessage />
          </FormControl>
        </SingleField>
        {isRealEstate && (
          <>
            <SingleField
              label={intl.formatMessage({
                id: "userSharedData.interbu.modal.steps.1.projectType",
              })}
              required
            >
              <FormControl isInvalid={!!errors.subtype}>
                <Controller
                  control={control}
                  name="subtype"
                  rules={{ required: true }}
                  render={({ field: { value, onChange, onBlur, ref }, fieldState }) => (
                    <ReactSelect
                      ref={ref}
                      onBlur={onBlur}
                      options={projectTypeOptions}
                      value={projectTypeOptions.filter(opt => value === opt.value)}
                      onChange={newVal => {
                        const subtype = newVal?.value ?? formState.subtype
                        setWatchedValue({ type: getValues("type"), subtype })
                        onChange(subtype)
                      }}
                      styles={useGetReactSelectStyles(fieldState)}
                      isLoading={isLoadingProjectType || isFetchingProjectType}
                      loadingMessage={() => intl.formatMessage({ id: "loading.generic.title" })}
                    />
                  )}
                />
                <RequiredErrorMessage />
              </FormControl>
            </SingleField>
            <SingleField
              label={intl.formatMessage({
                id: "userSharedData.interbu.modal.steps.1.propertyType",
              })}
              required
            >
              <FormControl isInvalid={!!errors.property_type}>
                <Controller
                  control={control}
                  name="property_type"
                  rules={{ required: true }}
                  render={({ field: { value, onChange, onBlur, ref }, fieldState }) => (
                    <ReactSelect
                      ref={ref}
                      onBlur={onBlur}
                      options={propertyTypeOptions}
                      value={propertyTypeOptions.filter(opt => value === opt.value)}
                      onChange={newVal => onChange(newVal?.value)}
                      styles={useGetReactSelectStyles(fieldState)}
                      isLoading={isLoadingPropertyType || isFetchingPropertyType}
                      loadingMessage={() => intl.formatMessage({ id: "loading.generic.title" })}
                    />
                  )}
                />
                <RequiredErrorMessage />
              </FormControl>
            </SingleField>
            {/** 
            <SingleField
              label={intl.formatMessage({
                id: "userSharedData.interbu.modal.steps.1.surface",
              })}
            >
              <MinMaxUnit
                minProps={{
                  defaultValue: formState.surface_min ?? undefined,
                }}
                maxProps={{
                  defaultValue: formState.surface_max ?? undefined,
                }}
                unitProps={{
                  options: unitsAreaOptions.map(c => ({
                    ...c,
                    value: c.value,
                  })),
                  defaultValue: unitsAreaOptions
                    .filter(opt => opt.value === formState.surface_unit)
                    .shift(),
                  customStyles: {
                    width: "120px",
                  },
                  isLoading: isLoadingUnitsArea || isFetchingUnitsArea,
                }}
                onChange={(min, max, unit) => {
                  setValue("surface_min", min)
                  setValue("surface_max", max)
                  setValue("surface_unit", unit)
                }}
              />
            </SingleField>
            **/}
            <SingleField
              label={intl.formatMessage({ id: "userSharedData.interbu.modal.steps.1.surface" })}
            >
              <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                <GridItem>
                  <Input
                    type="number"
                    {...register("surface_min")}
                    min="0"
                    max="999999999999"
                    placeholder={intl.formatMessage({ id: "min" })}
                    aria-label={intl.formatMessage({
                      id: "userSharedData.interbu.modal.steps.1.surface.min",
                    })}
                  />
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.surface_max}>
                    <Input
                      type="number"
                      {...register("surface_max", { validate: maxNotBelowMin("surface_min") })}
                      min="0"
                      max="999999999999"
                      placeholder={intl.formatMessage({ id: "max" })}
                      aria-label={intl.formatMessage({
                        id: "userSharedData.interbu.modal.steps.1.surface.max",
                      })}
                    />
                    <FormErrorMessage>{errors.surface_max?.message}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.surface_unit}>
                    <Controller
                      control={control}
                      name="surface_unit"
                      rules={{ required: true }}
                      render={({ field: { value, onChange, onBlur, ref }, fieldState }) => (
                        <ReactSelect
                          ref={ref}
                          onBlur={onBlur}
                          options={unitsAreaOptions}
                          value={unitsAreaOptions.filter(opt => value === opt.value)}
                          onChange={newVal => onChange(newVal?.value)}
                          styles={useGetReactSelectStyles(fieldState, { width: "100px" })}
                          isLoading={isLoadingUnitsArea || isFetchingUnitsArea}
                          loadingMessage={() => intl.formatMessage({ id: "loading.generic.title" })}
                        />
                      )}
                    />
                    <RequiredErrorMessage />
                  </FormControl>
                </GridItem>
              </Grid>
            </SingleField>
          </>
        )}
        <SingleField
          label={intl.formatMessage({
            id: priceLabelId(watchedValues.type, watchedValues.subtype),
          })}
        >
          <Grid templateColumns="repeat(3, 1fr)" gap={2}>
            <GridItem>
              <Input
                type="number"
                {...register("price_min")}
                min="0"
                max="999999999999"
                placeholder={intl.formatMessage({ id: "min" })}
                aria-label={intl.formatMessage({
                  id: "userSharedData.interbu.modal.steps.1.price.min",
                })}
              />
            </GridItem>
            <GridItem>
              <FormControl isInvalid={!!errors.price_max}>
                <Input
                  type="number"
                  {...register("price_max", { validate: maxNotBelowMin("price_min") })}
                  min="0"
                  max="999999999999"
                  placeholder={intl.formatMessage({ id: "max" })}
                  aria-label={intl.formatMessage({
                    id: "userSharedData.interbu.modal.steps.1.price.max",
                  })}
                />
                <FormErrorMessage>{errors.price_max?.message}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl isInvalid={!!errors.price_unit}>
                <Controller
                  control={control}
                  name="price_unit"
                  rules={{ required: true }}
                  render={({ field: { value, onChange, onBlur, ref }, fieldState }) => (
                    <ReactSelect
                      ref={ref}
                      onBlur={onBlur}
                      options={currenciesOptions}
                      value={currenciesOptions.filter(opt => value === opt.value)}
                      onChange={newVal => onChange(newVal?.value)}
                      styles={useGetReactSelectStyles(fieldState, { width: "100px" })}
                      isLoading={isLoadingCurrencies || isFetchingCurrencies}
                      loadingMessage={() => intl.formatMessage({ id: "loading.generic.title" })}
                    />
                  )}
                />
                <RequiredErrorMessage />
              </FormControl>
            </GridItem>
          </Grid>
        </SingleField>
        <SingleField
          label={intl.formatMessage({ id: "userSharedData.interbu.modal.steps.1.moreinfos" })}
          justifyContent="start"
        >
          <Textarea {...register("project_comment")} />
        </SingleField>
      </Flex>
      <Flex justifyContent="center" mt="100px" my={10}>
        <NextStepButton>
          <FormattedMessage id="userSharedData.interbu.modal.nextStep" />
        </NextStepButton>
      </Flex>
    </Box>
  )
}
